<template>
  <v-container fluid>

    <loading v-model="loading" />

    <v-snackbar v-model="msg.show" value="true" right top multi-line :color="msg.type" :timeout="msg.time">
      <div v-html="msg.text"></div>
      <v-btn dark flat @click="msg.show = false">
        {{ $t('OK') }}
      </v-btn>
    </v-snackbar>

    <v-card>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>{{ $t('Products available') }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <v-btn slot="activator" flat @click="clearFilters" color="grey darken-1" :disabled="!filtersOn || !Object.keys(filtersOn).length">
            <v-icon left>layers_clear</v-icon>
            {{ $t('Clear all filters') }}
          </v-btn>
          <span>{{ $t('Clear all filters') }}</span>
        </v-tooltip>

        <v-tooltip bottom><!-- TODO: extract to component -->
          <v-menu slot="activator"
            :close-on-content-click="false"
            :nudge-width="200"
            offset-x
            left
          >
            <v-btn icon slot="activator">
              <v-icon v-if="headers.some(header => !header.show)" class="text--secondary">settings_suggest</v-icon>
              <v-icon v-else class="text--secondary">settings</v-icon>
            </v-btn>

            <v-card>
              <v-list>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('Table settings') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ $t('Select visible columns') }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
              <v-divider></v-divider>
              <v-list dense>
                <v-list-tile v-for="(header,key) in headers.filter(header => header.value !== 'actions')" :key="'col-'+key" @click.prevent="columnToggle(header)">
                  <v-list-tile-action>
                    <v-switch :value="header.show" :input-value="header.show" color="primary" :disabled="columnHideCheck(header)"></v-switch>
                  </v-list-tile-action>
                  <v-list-tile-title>{{ header.text }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-card>
          </v-menu>
          <span>{{ $t('Table settings') }}</span>
        </v-tooltip>
      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="items"
        :pagination.sync="pagination"
        :total-items="totalItems"
        item-key="id"
        :loading="loading"
        :rows-per-page-items="[5, 10, 25, { text: $t('All'), value: -1 }]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('No data')"
        :no-results-text="$t('No results')"
        class="elevation-1"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate height="2" style="position: absolute; width: 100%; z-index: 10;"></v-progress-linear>
        </template>
        <template v-slot:headers="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'column sortable',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : '',
                'col-' + header.value]"
              class="px-2"
            >
              <template v-if="header.nofilter">
                <div @click="changeSort(header)">
                  <v-icon small>arrow_upward</v-icon>
                  {{ header.text }}
                </div>
              </template>

              <template v-else>
                <v-text-field
                  :label="header.text"
                  v-model="header.filter"
                  @keyup.enter="getItems()"

                  append-icon="search"
                  @click:append="getItems()"

                  clearable
                  @click:clear="header.filter = null; getItems()"

                  prepend-inner-icon="arrow_upward"
                  @click:prepend-inner="changeSort(header)"

                  hide-details
                  full-width background-color="grey lighten-4"
                ></v-text-field>
              </template>
            </th>
            <th class="text-xs-right blue lighten-3 white--text font-weight-bold col-input_quantity">
              {{ $t('Order') }}<br/>
              {{ $t('Qty') }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr :active="props.item.quantityInput > 0"
            :style="props.item.quantity > 0 ? '' : 'opacity: 0.3'"
          >
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="['text-xs-' + header.align, header.value.includes('quantity') ? 'blue lighten-5' : '']"
            >
              <template v-if="header.value === 'price'">
                {{ showAmount(props.item.price) }} <span v-if="props.item.alt_price" class="grey--text text--darken-1 font-weight-light ml-1">{{ showAmount(props.item.alt_price) }}</span>
              </template>
              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="text-xs-right blue lighten-3 px-1">
              <v-text-field
                :value="props.item.quantityInput"
                @input="props.item.quantityInput = Number($event); selectItem(props.item)"
                type="number"
                solo flat background-color="transparent"
                class="input-right"
                @click.stop
                :disabled="!props.item.quantity"
                :hide-details="rules.isQuantityLimits(props.item.quantityInput, (props.item.quantity))"
                :rules="[rules.quantityZero(props.item.quantityInput)]"
                :messages="rules.quantityMax(props.item.quantityInput, props.item.quantity)"
              ></v-text-field>
            </td>
          </tr>
        </template>
        <template v-slot:actions-prepend>
          <div class="layout align-center">
            <v-pagination v-model="pagination.page" circle :length="pages" total-visible="7" class="pagination"></v-pagination>
          </div>
        </template>
      </v-data-table>



      <v-toolbar flat color="transparent" class="pt-4">
        <v-toolbar-title>{{ $t('Selected for order') }} </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-menu v-model="receiver.isDialog"
          nudge-right
          :left="!$vuetify.breakpoint.xsOnly"
          :min-width="$vuetify.breakpoint.xsOnly ? '96%' : ''"
          :close-on-content-click="false"
          origin="top right"
          transition="scale-transition"
        >
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn v-on="{ ...tooltip, ...menu }" v-bind="attrs" outline color="success" :disabled="!selectedItems.length">
                  <v-icon left>expand_more</v-icon>
                  {{ $t('Order') }}
                </v-btn>
              </template>
              <span>{{ $t('Set a receiver data to order') }}</span>
            </v-tooltip>
          </template>
          <v-card max-width="640px">
            <v-form ref="receiverForm" v-model="receiver.isFormValid">
              <v-container fluid class="grey lighten-4">
                <v-layout row wrap align-center>
                  <v-flex>
                    <v-suggest-contact :label="$t('Target Partner')" v-model="target" :rules="[rules.vRequirePartnerCode]"></v-suggest-contact>
                    <!-- :own="contactSuggestOwn.target" @own-changed="contactSuggestOwn.target = $event; storePreferences('contactSuggestOwn')" -->
                  </v-flex>
                  <v-flex shrink class="pa-0">
                    <v-tooltip bottom>
                      <v-btn slot="activator" icon @click="processContact"><v-icon color="grey darken-1">group_add</v-icon></v-btn>
                      <span>{{ $t("Create new contact") }}</span>
                    </v-tooltip>
                    <v-dialog v-model="contact.show" transition="dialog-transition" persistent lazy max-width="900">
                      <v-card>
                        <v-card-text class="pa-0">
                          <contact id="0" v-model="contact.data" @close="processContactBack($event)"></contact>
                        </v-card-text>
                      </v-card>
                    </v-dialog>
                  </v-flex>
                </v-layout>
              </v-container>
              <v-container fluid grid-list-md>
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-text-field v-model="target.contact_person" :label="$t('ContactName')" prepend-inner-icon="person"></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-text-field ref="phoneInput" v-model="target.contact_phone" type="tel" :rules="vPhone" :hint="$t('Phone international hint')" :label="$t('Phone')" prepend-inner-icon="phone"></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-text-field v-model="target.contact_email" :label="$t('Email')" prepend-inner-icon="email"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm6>
                    <v-suggest-settlement :label="$t('Settlement')" v-model="target.address" :rules="[rules.vRequireCode]"></v-suggest-settlement>
                  </v-flex>
                  <v-flex xs12 sm6>
                    <v-text-field v-model="target.address.complex.name" :label="$t('Complex')"></v-text-field>
                  </v-flex>

                  <v-flex xs12 sm9>
                    <v-text-field v-model="target.address.street.name" :label="$t('Street')"></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm3>
                    <v-text-field v-model="target.address.street_number" label="№"></v-text-field>
                  </v-flex>

                  <v-flex xs6 sm3>
                    <v-text-field v-model="target.address.block" :label="$t('block')"></v-text-field>
                  </v-flex>
                  <v-flex xs6 sm3>
                    <v-text-field v-model="target.address.entrance" :label="$t('entrance')"></v-text-field>
                  </v-flex>
                  <v-flex xs6 sm3>
                    <v-text-field v-model="target.address.floor" :label="$t('floor')"></v-text-field>
                  </v-flex>
                  <v-flex xs6 sm3>
                    <v-text-field v-model="target.address.apartment" :label="$t('apartment')"></v-text-field>
                  </v-flex>

                  <v-flex xs12>
                    <v-text-field v-model="target.address.additional" :label="$t('Address additional')"></v-text-field>
                  </v-flex>
                </v-layout>

                <v-layout row wrap align-center>
                  <v-flex>
                    <v-text-field v-model="target.instructions" :label="$t('Instructions')"></v-text-field>
                  </v-flex>
                  <v-flex class="shrink text-sm-right">
                    <v-tooltip bottom>
                      <v-btn slot="activator" depressed @click="order" color="success" :disabled="!receiver.isFormValid">
                        <v-icon left>done</v-icon>
                        {{ $t('Order') }}
                      </v-btn>
                      <span>{{ $t('Create order to selected receiver') }}</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-card>
        </v-menu>

      </v-toolbar>

      <v-data-table
        :headers="headersShown"
        :items="selectedItems"
        item-key="id"
        :rows-per-page-items="[10, 25, 50]"
        :rows-per-page-text="$t('Rows per page')"
        :no-data-text="$t('No selected for order')"
        class="elevation-1"
        hide-headers
        hide-actions
      >
        <template v-slot:items="props">
          <tr :active="props.item.quantity == props.item.quantityInput">
            <td
              v-for="header in headersShown"
              :key="header.value"
              :class="[
                'text-xs-' + header.align,
                header.value.includes('quantity') ? 'blue lighten-5' : '',
                !!props.item.fixed ? 'error--text' : ''
              ]"
            >
              <template v-if="header.value === 'price'">
                {{ showAmountQty(props.item) }} <span v-if="props.item.alt_price" class="grey--text text--darken-1 font-weight-light ml-1"> {{ showAmountQty(props.item, 1) }}</span>
              </template>
              <template v-else-if="header.value === 'quantity'">
                {{ props.item.quantity }}
                <span class="grey--text">{{ quantityLeft(props.item) }}</span>
              </template>
              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="text-xs-right lighten-4 px-1" :class="!!props.item.fixed ? 'red' : 'blue'">
              {{ props.item.quantityInput }}
              <v-tooltip bottom>
                <v-btn slot="activator" icon flat @click="removeSelectedItem(props.item)" color="error">
                  <v-icon>remove_circle</v-icon>
                </v-btn>
                <span>{{ $t('Remove') }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>

    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { consumables }            from '@/resources';
import Loading                    from '@/components/Loading';
import { floatTrim, printDateTime, msg, extractBodyErrorMessages2, showAmount } from '@/components/util';

import Contact            from '@/views/Contact.vue';
import VSuggestContact    from '@/components/VSuggestContact.vue';
import VSuggestSettlement from '@/components/VSuggestSettlement.vue';

import { phoneRules, phoneCountry } from "../components/phone";

export default {
  name: 'consumables-products',
  components: {
    Loading,
    Contact,
    VSuggestContact,
    VSuggestSettlement
  },
  data() {
    return {
      loading: false,

      headers: [
        { text: this.$t('Name'),        value: 'name',             align: 'left',  sortable: true,  show: true, filter: undefined, persist: true },
        { text: this.$t('Price'),       value: 'price',            align: 'center', sortable: false,  show: true , nofilter: true } // off filter and sorter by price
        // { text: this.$t('Qty'),         value: 'quantity',      align: 'right', sortable: true,  show: true, filter: 1      },
      ],
      items: [],
      selectedItems: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'name',
        descending: false
      },
      rules: {
        quantityZero: (val) => val >= 0 || this.$t('Quantity cannot be negative'),
        quantityMax: (val, maxlimit) => val >= 0 && val <= maxlimit ? '' : (this.$t('Exceeds the initial value of', [maxlimit])),
        isQuantityLimits: (val, maxlimit) => val >= 0 && val <= maxlimit,
        vRequirePartnerCode: (v) => (!!v && !!v.partner && !!v.partner.code) || this.$t('This field is required!'),
        vRequireCode: (v) => (!!v && !!v.code) || this.$t('This field is required!')
      },
      // Target
      receiver: {
        item: {
          address: {
            complex: {},
            street: {}
          }
        },
        isDialog: false,
        isFormValid: false,
      },
      contact: { show: false, data: null },
      msg,
      floatTrim,
      printDateTime,
      showAmount
    }
  },
  computed: {
    vPhone() {
      return phoneRules(key => this.$t(key), { country: phoneCountry(this.target.address) });
    },
    ...mapGetters(['consumablesProducts']),

    pages() {
      if (this.pagination.rowsPerPage == null ||
        this.totalItems == null
      ) return 0;

      return Math.ceil(this.totalItems / this.pagination.rowsPerPage);
    },

    headersShown() {
      return this.headers.filter(elem => !!elem.show);
    },

    filtersOn() {
      let filters = {};
      this.headers.forEach(elem => {
        if (!!elem.filter) {
          if (elem.value === 'price') {
            ['price_min', 'price_max'].forEach(key2 => {

              if (elem.filter[key2]) {
                filters[key2] = elem.filter[key2];
              }

            });
          }
          else {
            filters[elem.value] = elem.filter;
          }
        }
      });
      return filters;
    },

    target: {
      get() {
        return this.receiver.item;
      },
      set(newValue) {
        this.receiver.item = newValue;
      }
    }
  },
  methods: {
    ...mapActions(['updateConsumablesProducts']),


    getItems(isPaginate, isIgnoreLoading) {
      if (this.loading && !isIgnoreLoading) {
        return;
      }
      this.loading = true;

      this.storeFilter();

      let params = this.filtersOn;

      let { sortBy, descending, page, rowsPerPage } = this.pagination;
      if (!isPaginate) {
        page = 1;
      }

      let offset = (page>=1 && rowsPerPage>=1) ? (page - 1) * rowsPerPage : 0;
      let length = (rowsPerPage>=1) ? rowsPerPage : -1;
      let sort   = (sortBy) ? (descending ? '-' : '') + sortBy : '';
      if (sort)
        params = Object.assign({ length, offset, sort }, params);
      else
        params = Object.assign({ length, offset }, params);

      consumables.list(params).then(response => {

        this.totalItems = response.body.length;
        this.items      = this.initializeItemResults(response.body);

      }).catch(response => {

        this.msg.popup( this.processResponsErr(response, this.$t('Error loading consumables list')), 'error');

      }).finally(() => {
        this.loading = false;
      });
    },
    initializeItemResults(response_body) {
      let results = JSON.parse(JSON.stringify(response_body.result));

      return results
        // Restore selected items
        .map(item => {
          let match = this.selectedItems.find(sitem => sitem.id === item.id);
          if (match) {
            item.quantityInput = match.quantityInput;
          }
          else {
            item.quantityInput = (!!item.quantity && Number(item.quantity) > 0 ? 0 : null);
          }
          return item;
        });
    },

    columnHideCheck(header) {
      if (header.persist)
        return true;

      if (!header.show)
        return false;

      let v = 0;
      for (let i = 0; i < this.headers.length; i++)
        if (this.headers[i].value != 'actions' && this.headers[i].show)
          v++;

      return (v<2);
    },
    columnToggle(header) {
      if (!header.persist) {
        header.show = !header.show;
        this.storeFilter();

        this.getItems();
      }
    },

    changeSort(header) {
      if (!header.sortable)
        return;
      let column = header.value;
      if (this.pagination.sortBy === column) {
        if (this.pagination.descending)
          this.pagination.sortBy = null;
        else
          this.pagination.descending = true;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    getPriceInput(header) {
      return (
        header.filter.price_min
          ? header.filter.price_min + '-' + header.filter.price_max
          : header.filter.price_max ? header.filter.price_max : ''
      )
    },
    setPriceInput(header, $event) {
      if (!$event) {
        header.filter = { price_min: undefined, price_max: undefined };
      }
      else {
        const split = $event.split('-');
        if (split[1]) {// has both
          header.filter = { price_min: split[0], price_max: split[1] };
        }
        else
        if (split[0]) {// has max
          header.filter = { price_min: undefined, price_max: split[0] };
        }
      }
    },
    clearPriceInput(header) {
      header.filter = { price_min: undefined, price_max: undefined };
    },

    storeFilter() {
      let s = {
        pagination:  this.pagination,
        headersdata: this.headers.filter(header => header.value !== 'actions')
          .map(header => {
            return {
              show: !!header.show,
              filter: header.filter
            }
          }),
      };
      this.updateConsumablesProducts(JSON.stringify(s));
    },

    showAmountQty(item, alt) {
      if (!item || !item.quantityInput) {
        return '';
      }
      let i = alt ? item.alt_price : item.price;
      if (!i) {
        return '';
      }
      let p = {
        ...i,
        amount: i.amount * item.quantityInput
      };
      return this.showAmount(p);
    },

    quantityLeft(item) {
      if (item.quantityInput > 0) {
        return ' / ' + (item.quantity - item.quantityInput) + ' ' + this.$t('rest');
      }
    },

    clearFilters() {
      this.headers.forEach(elem => {
        if (!elem.nofilter) {
          elem.filter = elem.value === 'quantity' ? 1 : undefined;
        }
        if (elem.value === 'price') {
          elem.filter = { price_min: undefined, price_max: undefined };
        }
      });
      this.getItems();
    },

    // Contact

    processContact() {// Convert contact fields from shipment to contact format
      let target = this.target;
      this.contact.data = {
        partner     : target.partner || {},
        address     : target.address,
        name        : target.contact_person,
        phone       : target.contact_phone,
        email       : target.contact_email,
        instructions: target.instructions
      };
      this.contact.show = true;
    },
    processContactBack(data) {
      if (data && typeof data === "object") {// Convert back
        this.target.address        = data.address;
        this.target.contact_person = data.name;
        this.target.contact_phone  = data.phone;
        this.target.contact_email  = data.email;
        this.target.instructions   = data.instructions;
        this.$set(this.target, 'partner', data.partner);
      }
      this.contact.show = false;
    },

    // ORDER (SELECTED ITEMS)

    selectItem(item) {
      let match = this.selectedItems.find(sitem => sitem.id === item.id);
      if (item.quantityInput > 0) {// && item.quantityInput <= item.quantity) {
        if (match) {
          match.quantityInput = item.quantityInput;
          match.fixed = false;
        }
        else {
          this.selectedItems.push({...item});
          this.selectedItems = this.selectedItems.filter(elem => elem.wms_station_code === item.wms_station_code);
        }
      }
      else if (match) {
        const matchIndex = this.selectedItems.findIndex(sitem => sitem.id === item.id);
        this.selectedItems.splice(matchIndex, 1);
      }
    },

    removeSelectedItem(sitemParam) {
      let match = this.items.find(item => item.id === sitemParam.id);
      if (match) {
        match.quantityInput = 0;
      }
      const matchIndex = this.selectedItems.findIndex(sitem => sitem.id === sitemParam.id);
      this.selectedItems.splice(matchIndex, 1);
    },

    removeSelectedItems(sitems) {
      (sitems || this.selectedItems).forEach(sitem => {
        let match = this.items.find(item => item.id === sitem.id);
        if (match) {
          match.quantityInput = 0;
        }
      });
      this.selectedItems = [];
    },

    order() {
      if (!this.$refs.receiverForm || !this.$refs.receiverForm.validate()) return;
      if (this.loading || !this.selectedItems.length) {
        return;
      }
      this.loading = true;

      const products = this.selectedItems.map(elem => {
        return {
          id:       elem.id,
          quantity: elem.quantityInput
        }
      });
      const target = JSON.parse(JSON.stringify(this.receiver.item));

      consumables.order({ consumables: products, target }).then(response => {

        let bodyText = '';
        let len = 0;
        if (response.body.consumables) {
          bodyText = response.body.consumables
                              .map(elem => elem.name + ' &times; ' + elem.quantity)
                              .join('<span class="px-2">&sdot;</span>');
          len = response.body.consumables.length;
        }
        this.msg.popup(this.$t('CONSUMABLES.CREATE_ORDER_SUCCESS', [response.body.id, len, bodyText]), 'success');
        this.removeSelectedItems();
        this.receiver.isDialog = false;

      }).catch(response => {

        this.msg.popup( this.processResponsErr(response, this.$t('Error creating order')), 'error');

      }).finally(() => {
        this.loading = false;
      });
    },

    processResponsErr(response, errorText) {
      return extractBodyErrorMessages2(response.body || errorText);
    },

  },
  watch: {
    vPhone() {
      // Vuetify 1 compares rule functions as objects and can miss changed closures.
      this.$nextTick(() => {
        if (this.$refs.phoneInput) this.$refs.phoneInput.validate();
      });
    },

    pagination: {
      handler() {
        this.getItems('isPaginate');
      },
      deep: true
    },
  },
  mounted() {
    {// reStore
      let storedFilter = null;
      try {
        storedFilter = JSON.parse(this.consumablesProducts);
      } catch(e) {
        console.log('Filter cannot be parsed', e);
        storedFilter = null;
      }

      if (storedFilter) {
        storedFilter.headersdata.forEach((elem, idx) => {
          if (typeof elem.show !== 'undefined') {
            this.headers[idx].show = elem.show;
          }
          if (typeof elem.filter !== 'undefined') {
            this.headers[idx].filter = elem.filter;
          }
        });

        if (storedFilter.pagination) {
          this.pagination = storedFilter.pagination;
        }
      }
    }
  }
}

</script>

<style scoped>
.input-right >>> input {
  text-align: right;
}
.pagination >>> button {
  box-shadow: none;
}
.col-quantity,
.col-input_quantity {
  width: 12rem;
}
[class*='col-'] > .v-input {
  min-width: 10rem
}
</style>